import { z } from "zod";

/**
 * Theme package format (the "contract") for the Nexus Cart Secure Theme
 * Engine. A theme package is a fully declarative description of a storefront
 * look: design tokens, a section/slot layout tree per region, optional
 * logic-limited templates, merchant-customizable settings, and scoped CSS.
 *
 * IMPORTANT SECURITY INVARIANT: a theme package contains NO author
 * JavaScript. The only "logic" an author may express is:
 *   - declarative section/slot composition rendered by platform-owned
 *     components (see `registry.ts`), and
 *   - logic-limited templates rendered by the sandboxed engine
 *     (see `template.ts`) — escaped, sanitized, and CSP-constrained.
 *
 * The engine ABI version. Bump when the package shape changes in a way that
 * is not backward compatible so older stored packages can be detected.
 */
export const THEME_ENGINE_VERSION = 1 as const;

/** A bilingual string. Arabic is the platform default; English required too. */
export const localizedSchema = z.object({
  ar: z.string(),
  en: z.string(),
});
export type Localized = z.infer<typeof localizedSchema>;

/**
 * Regions a theme can control. These map 1:1 to storefront surfaces. A theme
 * supplies an ordered section list for each region it wants to drive.
 */
export const THEME_REGIONS = [
  "header",
  "footer",
  "home",
  "product",
  "listing",
  "cart",
  "customPage",
] as const;
export type ThemeRegion = (typeof THEME_REGIONS)[number];

/**
 * A merchant-customizable setting. The settings schema is the contract
 * between a theme and the admin "customize" UI: it tells the admin what
 * knobs exist, their types, defaults, and bilingual labels.
 */
export const settingFieldSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "setting key must be a safe identifier"),
  type: z.enum(["color", "text", "textarea", "select", "boolean", "number", "font", "image"]),
  label: localizedSchema,
  help: localizedSchema.optional(),
  group: localizedSchema.optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
  /** For `select`: the allowed options with bilingual labels. */
  options: z
    .array(z.object({ value: z.string(), label: localizedSchema }))
    .optional(),
  /** For `number`: optional bounds. */
  min: z.number().optional(),
  max: z.number().optional(),
});
export type SettingField = z.infer<typeof settingFieldSchema>;

/**
 * Design tokens. Values may be literal CSS values OR a reference to a
 * merchant setting using the form `$setting:<key>`, which is resolved at
 * render time against the merged settings. Tokens compile to CSS custom
 * properties under the `--nx-*` namespace (see `tokens.ts`).
 */
export const themeTokensSchema = z.object({
  colors: z.record(z.string(), z.string()).default({}),
  fonts: z
    .object({
      ar: z.string().optional(),
      en: z.string().optional(),
      heading: z.string().optional(),
    })
    .default({}),
  radii: z.record(z.string(), z.string()).default({}),
  spacing: z.record(z.string(), z.string()).default({}),
});
export type ThemeTokens = z.infer<typeof themeTokensSchema>;

/**
 * A node in the section/slot layout tree. `component` must be a known
 * platform-owned component id (validated against the registry). `slots`
 * lets a component host child sections in named regions. `template`
 * (optional) names a sandboxed template used to render markup-only custom
 * content for slot-style components.
 *
 * `visible` controls show/hide: either a literal boolean or a reference to
 * a boolean-ish setting via `$setting:<key>` so merchants can toggle
 * elements/buttons on and off.
 */
export interface SectionNode {
  id: string;
  component: string;
  visible?: boolean | string;
  settings?: Record<string, unknown>;
  template?: string;
  slots?: Record<string, SectionNode[]>;
}

export const sectionNodeSchema: z.ZodType<SectionNode> = z.lazy(() =>
  z.object({
    id: z.string().min(1).max(64),
    component: z.string().min(1).max(96),
    visible: z.union([z.boolean(), z.string()]).optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    template: z.string().min(1).max(64).optional(),
    slots: z.record(z.string(), z.array(sectionNodeSchema)).optional(),
  }),
);

export const themeLayoutSchema = z.object({
  header: z.array(sectionNodeSchema).default([]),
  footer: z.array(sectionNodeSchema).default([]),
  home: z.array(sectionNodeSchema).default([]),
  product: z.array(sectionNodeSchema).default([]),
  listing: z.array(sectionNodeSchema).default([]),
  cart: z.array(sectionNodeSchema).default([]),
  customPage: z.array(sectionNodeSchema).default([]),
});
export type ThemeLayout = z.infer<typeof themeLayoutSchema>;

export const themeManifestSchema = z.object({
  id: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/, "theme id must be a slug (a-z, 0-9, -)"),
  name: localizedSchema,
  description: localizedSchema,
  version: z
    .string()
    .regex(/^\d+\.\d+\.\d+$/, "version must be semver MAJOR.MINOR.PATCH"),
  author: z.string().min(1).max(120),
  engineVersion: z.number().int().min(1),
  /** Merchant-customizable settings exposed to the admin customize UI. */
  settings: z.array(settingFieldSchema).default([]),
});
export type ThemeManifest = z.infer<typeof themeManifestSchema>;

/**
 * The full theme package. `styles` is raw author CSS; it is NEVER trusted —
 * the server scopes it, strips branding-anchor attacks, and runs it through
 * the AST watchdog before it ever reaches a browser. `templates` is a map of
 * template name -> sandboxed template source.
 */
export const themePackageSchema = z.object({
  manifest: themeManifestSchema,
  tokens: themeTokensSchema,
  layout: themeLayoutSchema,
  templates: z.record(z.string(), z.string()).default({}),
  styles: z.string().max(64 * 1024).default(""),
});
export type ThemePackage = z.infer<typeof themePackageSchema>;

export type ThemeValidationResult =
  | { ok: true; package: ThemePackage }
  | { ok: false; errors: string[] };

/**
 * Validate and normalize an untrusted theme package. Returns flattened,
 * human-readable error messages on failure. This does NOT validate the
 * component registry or template safety — callers should additionally run
 * `validateLayoutComponents` and template compilation for full assurance.
 */
export function validateThemePackage(input: unknown): ThemeValidationResult {
  const parsed = themePackageSchema.safeParse(input);
  if (!parsed.success) {
    const errors = parsed.error.issues.map(
      (i) => `${i.path.join(".") || "(root)"}: ${i.message}`,
    );
    return { ok: false, errors };
  }
  if (parsed.data.manifest.engineVersion > THEME_ENGINE_VERSION) {
    return {
      ok: false,
      errors: [
        `manifest.engineVersion ${parsed.data.manifest.engineVersion} is newer than supported ${THEME_ENGINE_VERSION}`,
      ],
    };
  }
  return { ok: true, package: parsed.data };
}

/** Reference to a setting value inside a token or `visible` field. */
export const SETTING_REF_PREFIX = "$setting:";

export function isSettingRef(value: unknown): value is string {
  return typeof value === "string" && value.startsWith(SETTING_REF_PREFIX);
}

export function settingRefKey(value: string): string {
  return value.slice(SETTING_REF_PREFIX.length);
}

/**
 * Coerce an arbitrary value to a boolean for show/hide evaluation. Mirrors the
 * permissive coercion used elsewhere so `"false"`/`"0"`/`""`/`"no"`/`"off"`
 * all read as hidden while everything else reads as visible.
 */
export function coerceBool(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return !(v === "" || v === "false" || v === "0" || v === "no" || v === "off");
  }
  return Boolean(value);
}

/**
 * Evaluate a layout node's `visible` field. Unset → visible. A literal boolean
 * is used directly. A `$setting:<key>` reference is resolved against the merged
 * settings and coerced to a boolean, so merchants can toggle any element/button
 * on or off from the customize UI.
 */
export function isNodeVisible(
  node: SectionNode,
  settings: Record<string, unknown>,
): boolean {
  const v = node.visible;
  if (v === undefined || v === null) return true;
  if (typeof v === "boolean") return v;
  if (isSettingRef(v)) return coerceBool(settings[settingRefKey(v)]);
  return coerceBool(v);
}

/**
 * Resolve any `$setting:<key>` references inside a node's `settings` object
 * against the merged settings, leaving literal values untouched. Lets layout
 * authors bind a component's props to merchant-customizable settings.
 */
export function resolveNodeSettings(
  node: SectionNode,
  settings: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const src = node.settings ?? {};
  for (const [k, val] of Object.entries(src)) {
    out[k] = isSettingRef(val) ? settings[settingRefKey(val)] : val;
  }
  return out;
}
